import React, { FC, useEffect, useState } from 'react';
import { NavLink } from 'react-router-dom';
import classNames from 'classnames';
import { useFeatureFlagEnabled, useFeatureFlagPayload } from 'posthog-js/react';

import { ChevronDown } from 'lucide-react';

import AdBanner from '../common/banner/adBanner';
import {
  FEATURE_FLAG_MENU_NOTIFICATIONS,
  FEATURE_FLAG_PLANS_V2,
  FEATURE_FLAG_TOOL_COLOR_CODE,
  FEATURE_FLAG_TOOL_CLOUDS,
  FEATURE_FLAG_TOOL_SAND_THERAPY,
} from '../../constants/featureFlags';

import { getNotificationsForMenuItem, parseMenuNotificationsPayload } from './menu-notification-data';
import { MenuBellTrailing, MenuNotificationTrailing } from './menu-notification-trailing';

const itemBase =
  'w-full flex items-center justify-between gap-2 px-4 py-3 rounded-xl text-[13px] leading-snug font-medium transition-colors text-left';

const itemInactive = 'bg-gray-100 text-gray-700 hover:bg-gray-200';
const itemActive = 'bg-[#C41230] text-white';

interface DesktopMenuProps {
  path: string;
}

export const DesktopMenu: FC<DesktopMenuProps> = ({ path }) => {
  const [isToolsOpen, setIsToolsOpen] = useState(path.includes('tools'));
  const isToolsRoute = path.includes('tools');
  const menuNotificationsPayload = useFeatureFlagPayload(FEATURE_FLAG_MENU_NOTIFICATIONS);
  const isColorCodeEnabled = useFeatureFlagEnabled(FEATURE_FLAG_TOOL_COLOR_CODE);
  const isCloudsEnabled = useFeatureFlagEnabled(FEATURE_FLAG_TOOL_CLOUDS);
  const isSandTherapyEnabled = useFeatureFlagEnabled(FEATURE_FLAG_TOOL_SAND_THERAPY);
  const isPlansV2Enabled = useFeatureFlagEnabled(FEATURE_FLAG_PLANS_V2);

  useEffect(() => {
    if (isToolsRoute) {
      setIsToolsOpen(true);
    } else {
      setIsToolsOpen(false);
    }
  }, [isToolsRoute]);

  const toolsIsHighlighted = isToolsOpen;

  const menuNotifications = parseMenuNotificationsPayload(menuNotificationsPayload);
  const subLinkClass = ({ isActive }: { isActive: boolean }) =>
    classNames(
      'w-full rounded-lg px-3 py-2 text-[13px] leading-snug font-medium transition-colors text-left text-gray-700 hover:bg-gray-200/80',
      isActive && `${itemActive} hover:bg-[#C41230]`,
    );

  const toolsNotifications = getNotificationsForMenuItem('tools', menuNotifications);
  const tariffsNotifications = getNotificationsForMenuItem('tariffs', menuNotifications);
  const infoNotifications = getNotificationsForMenuItem('info', menuNotifications);

  return (
    <aside className="hidden lg:flex lg:flex-col lg:shrink-0 w-80 min-w-80 min-h-0 px-4 gap-2 bg-white mb-2.5 pt-[24px]">
      <NavLink to="/profile" className={({ isActive }) => classNames(itemBase, isActive ? itemActive : itemInactive)}>
        <span className="min-w-0 flex-1 text-left">Профиль</span>
      </NavLink>
      <NavLink
        to="/consultations"
        className={({ isActive }) => classNames(itemBase, isActive || path === 'settings' ? itemActive : itemInactive)}
      >
        <span className="min-w-0 flex-1 text-left">Создать встречу</span>
      </NavLink>
      <NavLink to="/journal" className={({ isActive }) => classNames(itemBase, isActive ? itemActive : itemInactive)}>
        <span className="min-w-0 flex-1 text-left">Терапевтический журнал</span>
      </NavLink>

      <div className="relative w-full min-w-0 max-w-full">
        <NavLink
          to="/tools"
          className={({ isActive }) =>
            classNames(
              itemBase,
              'pr-24',
              toolsIsHighlighted || isActive ? itemActive : itemInactive,
              isToolsOpen && '!rounded-b-none',
            )
          }
        >
          <span className="min-w-0 flex-1 text-left">Инструменты</span>
        </NavLink>
        <div className="absolute right-3 top-1/2 z-10 flex -translate-y-1/2 items-center gap-2 whitespace-nowrap">
          <MenuBellTrailing notifications={toolsNotifications} isActive={toolsIsHighlighted} />
          <button
            type="button"
            aria-label="Переключить список инструментов"
            onClick={() => setIsToolsOpen((prev) => !prev)}
            className="rounded p-0.5 transition-transform focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-red-500 focus-visible:ring-offset-2"
          >
            <ChevronDown
              size={16}
              className={classNames(
                'transition-transform',
                toolsIsHighlighted ? 'text-white' : 'text-gray-500',
                isToolsOpen ? 'rotate-180' : 'rotate-0',
              )}
            />
          </button>
        </div>
      </div>

      <div
        className={classNames('hidden w-full min-w-0 max-w-full self-stretch overflow-hidden flex-col gap-1.5 rounded-b-xl bg-gray-100 px-3 py-3 -mt-4', {
          '!flex': isToolsOpen,
        })}
      >
        <NavLink to="/tools/ai-assistants" className={subLinkClass}>
          ИИ Ассистенты
        </NavLink>
        <NavLink to="/tools/emotion-detector" className={subLinkClass}>
          Детектор эмоций
        </NavLink>
        <NavLink to="/tools/metaphorical-cards" className={subLinkClass}>
          Метафорические карты
        </NavLink>
        <NavLink to="/tools/question" className={subLinkClass}>
          Подкинь вопрос
        </NavLink>
        <NavLink to="/tools/dpdg" className={subLinkClass}>
          ДПДГ онлайн
        </NavLink>
        <NavLink to="/tools/life-balance-wheel" className={subLinkClass}>
          Колесо баланса
        </NavLink>
        <NavLink to="/tools/ai-grow" className={subLinkClass}>
          GROW AI
        </NavLink>
        {isColorCodeEnabled && (
          <NavLink to="/tools/color-code" className={subLinkClass}>
            Цветовой код
          </NavLink>
        )}
        {isCloudsEnabled && (
          <NavLink to="/tools/clouds" className={subLinkClass}>
            Облака
          </NavLink>
        )}
        {isSandTherapyEnabled && (
          <NavLink to="/tools/sand-therapy" className={subLinkClass}>
            Песочная терапия
          </NavLink>
        )}
        {isSandTherapyEnabled && (
          <NavLink to="/tools/arrangements" className={subLinkClass}>
            Расстановки
          </NavLink>
        )}
      </div>

      {isPlansV2Enabled ? (
        <NavLink
          to="/subscriptions"
          className={({ isActive }) => classNames(itemBase, isActive ? itemActive : itemInactive)}
        >
          <span className="min-w-0 flex-1 text-left">Мои подписки</span>
        </NavLink>
      ) : (
        <NavLink to="/tariffs" className={({ isActive }) => classNames(itemBase, isActive ? itemActive : itemInactive)}>
          {({ isActive }) => (
            <>
              <span className="min-w-0 flex-1 text-left">Тарифы и бонусы</span>
              <MenuNotificationTrailing notifications={tariffsNotifications} isActive={isActive} />
            </>
          )}
        </NavLink>
      )}
      <NavLink to="/info" className={({ isActive }) => classNames(itemBase, isActive ? itemActive : itemInactive)}>
        {({ isActive }) => (
          <>
            <span className="min-w-0 flex-1 text-left">Информация</span>
            <MenuNotificationTrailing notifications={infoNotifications} isActive={isActive} />
          </>
        )}
      </NavLink>
      <NavLink
        to="/referal-management"
        className={({ isActive }) => classNames(itemBase, isActive ? itemActive : itemInactive)}
      >
        <span className="min-w-0 flex-1 text-left">Ваш ключ</span>
      </NavLink>

      <div className="mt-2">
        <AdBanner />
      </div>
    </aside>
  );
};

export default DesktopMenu;
